/**
 * Bulk device import dialog. Download a template, upload a CSV/XLSX, see a
 * result summary (created / errors). Posts to /devices/import.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Alert, List, ListItem, ListItemText, LinearProgress, Link, Chip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import axios from '../api/axios';

export default function ImportDevicesDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const reset = () => { setFile(null); setResult(null); setError(null); setBusy(false); };
  const close = () => { reset(); onClose(); };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get('/devices/import/template', { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = 'devices_import_template.csv';
      a.click();
    } catch (e) { setError('Не удалось скачать шаблон'); }
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post('/devices/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      if (res.data.created > 0 && onImported) onImported();
    } catch (e) {
      setError(e.response?.data?.detail || 'Ошибка импорта');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Импорт устройств из CSV / Excel</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Колонки: <b>name</b>, <b>type</b> (точное имя типа устройства), location, description, ip.
          Тип устройства должен уже существовать.
        </Typography>
        <Button size="small" startIcon={<DownloadIcon />} onClick={downloadTemplate} sx={{ mb: 2 }}>
          Скачать шаблон
        </Button>

        <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 2, textAlign: 'center' }}>
          <input id="imp-file" type="file" accept=".csv,.xlsx,.xlsm" hidden
                 onChange={(e) => { setFile(e.target.files[0]); setResult(null); }} />
          <label htmlFor="imp-file">
            <Button component="span" startIcon={<UploadFileIcon />} variant="outlined">Выбрать файл</Button>
          </label>
          {file && <Chip label={file.name} sx={{ ml: 1 }} onDelete={() => setFile(null)} />}
        </Box>

        {busy && <LinearProgress sx={{ mt: 2 }} />}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        {result && (
          <Box sx={{ mt: 2 }}>
            <Alert severity={result.error_count ? 'warning' : 'success'}>
              Создано: {result.created} из {result.total_rows}. Ошибок: {result.error_count}.
            </Alert>
            {result.errors?.length > 0 && (
              <List dense sx={{ maxHeight: 200, overflow: 'auto', mt: 1 }}>
                {result.errors.map((er, i) => (
                  <ListItem key={i}><ListItemText primary={`Строка ${er.row}: ${er.reason}`} /></ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Закрыть</Button>
        <Button variant="contained" onClick={upload} disabled={!file || busy}>Импортировать</Button>
      </DialogActions>
    </Dialog>
  );
}
